import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconButton, TableCell, TableRow } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getStringValue } from 'util/Format';
import { TableColumn } from 'store/types/Table';
import { TableRowProps } from './Table';

import styles from './Table.module.scss';

interface RowProps<T> {
  record: T;
  index: number;
  columns: Array<TableColumn<T>>;
  cellClassName?: string;
  rowProps?: TableRowProps<T>;
  showChildren?: boolean;
}

type TableRowComponentType<T = any> = React.FunctionComponent<RowProps<T>>;

const Row: TableRowComponentType = ({ record, index, columns, rowProps, cellClassName, showChildren }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = useMemo(() => !!record.children && record.children.length > 0, [record]);
  const rowTestId = useMemo(() => `table-row-${index}`, [index]);

  useEffect(() => {
    if (hasChildren) {
      setOpen(true);
    }
  }, [hasChildren]);

  const classes = useMemo(
    () => ({
      root: styles.row,
      selected: styles.selected,
    }),
    []
  );

  const tableRowProps = useMemo(
    () =>
      rowProps
        ? {
            role: 'checkbox',
            selected: rowProps.selected(record),
            hover: true,
          }
        : {},
    [rowProps, record]
  );

  const getCells = useCallback(
    (record: any, index: number): React.ReactNode[] =>
      columns.map(({ hidden, render, dataIndex = '', key = '', align, rowAlign, verticalAlign }) => {
        const cellTestId = `table-cell-${dataIndex.toString() || key}-${index}`;

        return (
          <TableCell
            sx={{ verticalAlign }}
            hidden={hidden}
            align={rowAlign ?? align}
            key={cellTestId}
            data-testid={cellTestId}
            className={cellClassName}
          >
            {render ? render(record[dataIndex], record) : getStringValue(record[dataIndex])}
          </TableCell>
        );
      }),
    [cellClassName, columns]
  );

  return (
    <>
      <TableRow classes={classes} key={rowTestId} data-testid={rowTestId} {...tableRowProps}>
        {showChildren && (
          <TableCell>
            {hasChildren && (
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
        )}
        {getCells(record, index)}
      </TableRow>

      {showChildren &&
        hasChildren &&
        open &&
        record.children.map((child: any, childIndex: number) => {
          const childRowTestId = `table-row-${index}-${childIndex}`;
          return (
            <TableRow classes={classes} key={childRowTestId} data-testid={childRowTestId}>
              <TableCell />
              {getCells(child, childIndex)}
            </TableRow>
          );
        })}
    </>
  );
};

export default Row;
